import { create } from "zustand";

interface IInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

type IInstallStatus = "idle" | "prompting" | "accepted" | "dismissed" | "error";

interface IInstallState {
  available: boolean;
  installed: boolean;
  status: IInstallStatus;
}

export const usePwaInstallStore = create<IInstallState>(() => ({
  available: false,
  installed: false,
  status: "idle",
}));

let deferredPrompt: IInstallPromptEvent | null = null;
let initialized = false;

function capturePrompt(event: Event) {
  event.preventDefault();
  deferredPrompt = event as IInstallPromptEvent;
  // A fresh browser event also handles a later uninstall/reinstall.
  usePwaInstallStore.setState({
    available: true,
    installed: false,
    status: "idle",
  });
}

function installed() {
  deferredPrompt = null;
  usePwaInstallStore.setState({
    installed: true,
    available: false,
    status: "idle",
  });
}

export function initializePwaInstall() {
  if (initialized) return;

  initialized = true;
  const displayMode = window.matchMedia(
    "(display-mode: standalone), (display-mode: fullscreen), (display-mode: minimal-ui)",
  );
  const isStandalone = () =>
    displayMode.matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const updateDisplayMode = () => {
    if (isStandalone()) {
      deferredPrompt = null;
      usePwaInstallStore.setState({ installed: true, available: false });
    }
  };

  updateDisplayMode();
  window.addEventListener("beforeinstallprompt", capturePrompt);
  window.addEventListener("appinstalled", installed);
  displayMode.addEventListener("change", updateDisplayMode);

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      window.removeEventListener("beforeinstallprompt", capturePrompt);
      window.removeEventListener("appinstalled", installed);
      displayMode.removeEventListener("change", updateDisplayMode);
    });
  }
}

// Call only from a click: redirects cannot supply browser user activation.
export async function installPwa() {
  const prompt = deferredPrompt;

  if (!prompt || usePwaInstallStore.getState().status === "prompting") return;

  // A browser install event can be used only once.
  deferredPrompt = null;
  usePwaInstallStore.setState({ available: false, status: "prompting" });
  try {
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;

    if (!usePwaInstallStore.getState().installed) {
      usePwaInstallStore.setState({ status: outcome });
    }
  } catch {
    usePwaInstallStore.setState({ status: "error" });
  }
}

export function getInstallPlatform(
  userAgent = navigator.userAgent,
  touchPoints = navigator.maxTouchPoints,
) {
  if (
    /iPad|iPhone|iPod/.test(userAgent) ||
    (/Macintosh/.test(userAgent) && touchPoints > 1)
  )
    return "ios";
  if (/Android/.test(userAgent)) return "android";
  if (
    /Macintosh/.test(userAgent) &&
    /Safari/.test(userAgent) &&
    !/Chrome|Chromium|Edg/.test(userAgent)
  )
    return "safari";

  return "desktop";
}
